/**
 * Trade Loop v2.0 - Main Entry Point
 *
 * Cookie-based authentication with React Router DOM
 *
 * RELIABILITY FEATURES:
 * - Initial loading indicator in index.html (shown before JS loads)
 * - Error boundary catches React errors
 * - Graceful degradation if auth check fails
 * - Service worker recovery mechanism
 */

// Initialize Sentry before any other imports for best error coverage
import { initSentry } from './lib/sentry';
initSentry();

// Bundled native builds serve the app from https://localhost, so relative
// `fetch('/api/...')` calls would hit Capacitor's asset server (which answers
// with the SPA fallback) instead of the API. Install before any request runs.
// No-op on web.
import { installNativeApiOriginShim } from './lib/nativeApiOrigin';
installNativeApiOriginShim();

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './components/ThemeProvider';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import App from './App';
import './index.css';
import './styles/referral-view-filters.css';
import '../../../config/ui/theme/theme-styles.css';

/**
 * AppInitializer - Handles the transition from initial HTML loader to React app
 * Ensures users always see visual feedback during app initialization
 */
function AppInitializer({ children }: { children: React.ReactNode }) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // React mounted, so this build loaded cleanly. Clear the one-shot reload guards, which
    // only ever unblock here: a reload loop implies React never mounts, so a looping client
    // leaves them set and stops reloading, while a healthy one stays free to auto-update.
    sessionStorage.removeItem('tl-boot-recovery');
    sessionStorage.removeItem('tl-sw-reloaded');

    // Remove the initial loader from index.html now that React has mounted
    const initialLoader = document.getElementById('initial-loader');
    if (initialLoader) {
      // Fade out the loader
      initialLoader.style.transition = 'opacity 0.2s ease-out';
      initialLoader.style.opacity = '0';
      setTimeout(() => {
        initialLoader.remove();
        setIsReady(true);
      }, 200);
    } else {
      setIsReady(true);
    }

    // Add a class to indicate React has mounted (useful for debugging)
    document.documentElement.classList.add('react-mounted');

    return () => {
      document.documentElement.classList.remove('react-mounted');
    };
  }, []);

  // Show nothing briefly while we transition from HTML loader to React
  // This prevents any flash of unstyled content
  if (!isReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Root Error Fallback - Shown if ErrorBoundary catches an error
 * Provides recovery options including service worker cache clear
 */
function RootErrorFallback() {
  const handleHardRefresh = async () => {
    // Clear service worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }

    // Hard refresh
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
        <p className="text-gray-600 mb-6">
          The application encountered an error. This might be due to a cached version issue.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
          <button
            onClick={handleHardRefresh}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear Cache & Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

// Render the app with all providers
const rootElement = document.getElementById('root');
if (!rootElement) {
  // This should never happen, but handle it gracefully
  document.body.innerHTML = '<div style="padding: 20px; text-align: center;">Failed to find root element. Please refresh the page.</div>';
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary fallback={<RootErrorFallback />}>
        <AppInitializer>
          <QueryClientProvider client={queryClient}>
            {/* TODO: React Router v7 Migration - Add future flags to BrowserRouter:
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                See: https://reactrouter.com/v6/upgrading/future */}
            <BrowserRouter>
              <ThemeProvider defaultMode="system">
                <ToastProvider logoSrc="/logo-no-text.svg">
                  <AuthProvider>
                    <App />
                  </AuthProvider>
                </ToastProvider>
              </ThemeProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </AppInitializer>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

import { initializeBackgroundSync } from './services/axiosInterceptors';
import { initializeCapacitor } from './services/capacitor';
import { teardownServiceWorker } from './utils/serviceWorkerTeardown';

// The PWA is retired. Nothing registers a service worker any more — not on web, not inside
// the Capacitor WebView — and this actively removes the one previous builds installed, along
// with its caches. Existing web installs are additionally taken over by the tombstone worker
// served at /sw-push.js, which is what reaches a client this code cannot run in.
void teardownServiceWorker();

initializeBackgroundSync();

// Initialize native Capacitor plugins (splash screen, status bar, keyboard,
// haptics, app lifecycle). No-ops on web via its internal isNative() guard.
initializeCapacitor();
